import { useRoomSettings } from "../context";
import { Badge, BadgeProps, IconButton, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SidebarState } from "../types";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { ControlBarIconContainer } from "../widgets";
import { useChatMessages } from "../chatMessages";
import { messageContentRenderers } from "./chat/messageContentRenderers";
import { defineMessages, FormattedMessage } from "react-intl";

const sidebarStateMessages = defineMessages<SidebarState>({
    hidden: {
        id: "meetingPage.room.sidebarSelector.hidden",
        defaultMessage: "Show sidebar",
    },
    chat: {
        id: "meetingPage.room.sidebarSelector.chat",
        defaultMessage: "{on, plural, =0 {Show} other {Hide}} chat",
    },
    participant: {
        id: "meetingPage.room.sidebarSelector.participant",
        defaultMessage: "{on, plural, =0 {Show} other {Hide}} participants",
    },
});

const StateButton = ({ state, icon, badgeProps }: { state: SidebarState; icon: IconProp; badgeProps?: BadgeProps }) => {
    const roomSettings = useRoomSettings();

    const isOn = roomSettings.sidebarState === state;

    return (
        <Tooltip title={<FormattedMessage {...sidebarStateMessages[state]} values={{ on: isOn ? 1 : 0 }} />} arrow>
            <IconButton
                color={isOn ? "primary" : "default"}
                onClick={() => {
                    roomSettings.sidebarState = isOn ? "hidden" : state;
                }}
                size="large"
            >
                <ControlBarIconContainer>
                    <Badge {...badgeProps}>
                        <FontAwesomeIcon icon={icon} />
                    </Badge>
                </ControlBarIconContainer>
            </IconButton>
        </Tooltip>
    );
};

const renderableMessageTypes = Object.keys(messageContentRenderers);

const useUnseenMessagesCount = (): number => {
    const roomSettings = useRoomSettings();
    const { messages } = useChatMessages();

    const renderableMessageIds = messages
        .filter((m) => renderableMessageTypes.includes(m.contentType))
        .map((m) => m.messageId);

    if (!roomSettings.lastSeenMessageId) return renderableMessageIds.length;

    return renderableMessageIds.length - renderableMessageIds.indexOf(roomSettings.lastSeenMessageId) - 1;
};

export const ParticipantStateButton = () => <StateButton icon="users" state="participant" />;

export const ChatStateButton = () => {
    const unseenMessagesCount = useUnseenMessagesCount();
    return (
        <StateButton
            icon="comments"
            state="chat"
            badgeProps={{
                badgeContent: unseenMessagesCount,
                color: "primary",
            }}
        />
    );
};
